import { Intent, NumericInput } from "@blueprintjs/core";
import { isEmpty } from "lodash";
import { useCallback, useState } from "react";
import { IInputProps } from "../types";
import { INumber } from "components/Template/types";
import { doDisabled, doHidden, doInvalid } from "components/Template/utils";
import Validate from "components/Template/Validate";
import Override from "components/Template/Override";
import ChangeHighlight from "components/Template/ChangeHighlight";
import "./Number.scss";

export interface INumberProps extends IInputProps {
  template: INumber;
}

export const checkValue = (template: INumber, v: string) => {
  try {
    const t = parseFloat(v);
    if (!isFinite(t)) {
      return `Entered value must be a valid number.`;
    } else if (t < template.min) {
      return `Entered value must be equal or greater than ${template.min}`;
    } else if (t > template.max) {
      return `Entered value must be equal or less than ${template.max}`;
    } else {
      return "";
    }
  } catch (e) {
    return `Entered value must be a valid number.`;
  }
};

const Number = (props: INumberProps) => {
  const { template, mutators, metadata } = props;
  const { getValue, handleChange } = mutators;

  const [value, setValue] = useState({
    n: getValue(template.name) as number,
    s: `${getValue(template.name) as number}`,
  });
  const [fault, setFault] = useState("");

  const transformInput = useCallback(
    (n: number, s: string) => {
      const f = checkValue(template, s);
      setValue({ n, s });
      setFault(f);
      return n;
    },
    [template]
  );

  if (doHidden(props)) {
    if (!metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput.push(template.name);
    }
    return null;
  } else {
    if (metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput = metadata.hiddenInput.filter((i) => i === template.name);
    }
  }
  const v = getValue(template.name);
  const storedValue = mutators.getStoredValue ? mutators.getStoredValue(template.name) : undefined;
  const hasChanged = storedValue !== undefined && storedValue !== v;

  const handleUndo = () => {
    if (mutators.setStoredValue && storedValue !== undefined && handleChange) {
      handleChange(template.name, storedValue);
    }
  };

  const handleKeep = () => {
    if (mutators.setStoredValue) {
      mutators.setStoredValue(template.name, v);
    }
  };
  const faulted = !isEmpty(fault);
  const disabled = doDisabled(props);
  const invalid = doInvalid(props);
  return (
    <Validate
      {...props}
      className="number"
      intent={faulted ? Intent.DANGER : Intent.NONE}
      helperText={faulted ? fault : ""}
      showLabel
      showInfo
    >
      <ChangeHighlight
        changed={hasChanged}
        onUndo={hasChanged ? handleUndo : undefined}
        onKeep={hasChanged ? handleKeep : undefined}
        changePath={mutators.getPath(template.name)}
      >
        <div className="input-with-button">
          <NumericInput
            key={template.name}
            intent={invalid || faulted ? Intent.DANGER : Intent.NONE}
            id={template.name}
            min={template.min}
            max={template.max}
            stepSize={template.step}
            minorStepSize={null}
            majorStepSize={null}
            value={v !== value.n ? `${v}` : value.s}
            selectAllOnFocus
            selectAllOnIncrement
            leftIcon={template.leftIcon}
            {...(handleChange
              ? {
                  onValueChange: (v, t) => handleChange(template.name, transformInput(v, t)),
                }
              : { readOnly: true })}
            disabled={disabled}
          />
          {template.info ? <span className="unit"> {template.info}</span> : ""}
          <Override {...props} />
        </div>
      </ChangeHighlight>
    </Validate>
  );
};

export default Number;
